import React, {useState} from 'react';

import Col from 'ui-library/bs/Col';
import FormGroup from 'ui-library/bs/FormGroup';
import {TextInputType} from 'ui-library/inputs/InputConstants';
import TextInput from 'ui-library/inputs/TextInput';
import Modal from 'ui-library/modals/Modal';
import {ElementMouseEvent} from 'util/Types';

import {TABLE_NAME_MAX_CHARS} from '../TableConstants';

const getError = (
  tableName: string,
  isTableNameUsed: (tableName: string) => boolean,
  initialTableName: string
): string | null => {
  if (tableName.length === 0) {
    return 'Table name cannot be empty';
  }
  if (tableName.length > TABLE_NAME_MAX_CHARS) {
    return `Table name exceeds ${TABLE_NAME_MAX_CHARS} characters`;
  }
  if (isTableNameUsed(tableName) && tableName !== initialTableName) {
    return 'Table name already in use';
  }
  return null;
};

const RENAME_TABLE_MODAL_LABEL_ID = 'RENAME_TABLE_LABEL';

export default function RenameTableModal({
  initialTableName = '',
  onTableRenamed,
  isTableNameUsed,
}: {
  initialTableName?: string;
  isTableNameUsed: (tableName: string) => boolean;
  onTableRenamed: (tableName: string | null) => void;
}): React.ReactElement {
  const [tableName, setTableName] = useState(initialTableName);

  const handleRenameTable = (): void => {
    if (getError(tableName, isTableNameUsed, initialTableName) != null) {
      return;
    }
    if (tableName === initialTableName) {
      onTableRenamed(null);
      return;
    }
    onTableRenamed(tableName);
  };

  const error = getError(tableName, isTableNameUsed, initialTableName);

  return (
    <div
      onMouseDown={(e: ElementMouseEvent) => {
        e.stopPropagation();
      }}
    >
      <Modal
        title="Rename table"
        onRequestClose={() => {
          onTableRenamed(null);
        }}
        onRequestSubmit={() => {
          handleRenameTable();
        }}
        buttons={[{text: 'Save', submit: true, disabled: error != null}]}
      >
        <div className="form-horizontal">
          <FormGroup>
            <Col sm={12}>
              <label className="u-tiny-spacing-bottom" id={RENAME_TABLE_MODAL_LABEL_ID}>
                Table Name
              </label>
              {error != null ? <span className="u-danger-color u-tiny-spacing-left">{error}</span> : null}
              <TextInput
                aria-labelledby={RENAME_TABLE_MODAL_LABEL_ID}
                type={TextInputType.TEXT}
                value={tableName}
                onChange={(e) => {
                  setTableName(e.target.value);
                }}
                className="form-control"
                autoFocus={true}
                initialSelect={true}
              />
            </Col>
          </FormGroup>
        </div>
      </Modal>
    </div>
  );
}
